import {
  DatePicker,
  DatePickerLocales,
  DatePickerValue,
} from '@design/components/date-picker/DatePicker';
import {
  offset,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { Filters } from '@terminal/components/filters/Filters';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNewsSearchStore } from '../../stores/use-news-search-store/useNewsSearchStore';
import {
  formatDateRange,
  TEMP_MIN_FETCHING_NEWS_DATE,
} from '../../utils/utils';
import { ClearFiltersButton } from '../clear-filters-button/ClearFiltersButton';
import { MoreFilters } from './components/more-filters/MoreFilters';
import styles from './SearchFilters.module.scss';

const DATE_PICKER_Y_AXIS_OFFSET_PX = 5;

export const SearchFilters = () => {
  const { t } = useTranslation(['news', 'datePicker']);

  const datePickerLocales: DatePickerLocales = {
    selectYear: t('datePicker:select_year'),
    selectMonth: t('datePicker:select_month'),
    selectDecade: t('datePicker:select_decade'),
    previousMonth: t('datePicker:previous_month'),
    previousYear: t('datePicker:previous_year'),
    previousDecade: t('datePicker:previous_decade'),
    nextMonth: t('datePicker:next_month'),
    nextYear: t('datePicker:next_year'),
    nextDecade: t('datePicker:next_decade'),
  };
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValues, setDatePickerValues] = useState<DatePickerValue>([
    null,
    null,
  ]);

  const {
    accordionFilters,
    activeFiltersCount,
    activeAccordionFiltersCount,
    clearActiveFilters,
    filterGroups,
    toggleFilterOption,
  } = useNewsSearchStore(state => ({
    accordionFilters: state.accordionFilters,
    activeFiltersCount: state.getActiveFiltersCount(),
    activeAccordionFiltersCount: state.getActiveAccordionFiltersCount(),
    clearActiveFilters: state.clearActiveFilters,
    filterGroups: state.filterGroups,
    toggleFilterOption: state.toggleFilterOption,
  }));

  const { addFilterOption } = useFilterActions({
    store: useNewsSearchStore,
    group: 'NEWS_SEARCH_FILTERS',
  });

  const { refs, floatingStyles, context } = useFloating({
    open: showDatePicker,
    onOpenChange: setShowDatePicker,
    placement: 'bottom-start',
    middleware: [
      offset({
        mainAxis: DATE_PICKER_Y_AXIS_OFFSET_PX,
      }),
    ],
  });

  const openDatePicker = () => {
    const triggerElement = document.querySelector(
      'button[data-filter="date-picker"]'
    );

    refs.setPositionReference(triggerElement);
    setShowDatePicker(true);
  };

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context),
  ]);

  const setFilterOption = (dateRange: DatePickerValue) => {
    if (Array.isArray(dateRange)) {
      const [from, to] = dateRange;

      if (from && to) {
        const name = formatDateRange([from, to]);

        addFilterOption({
          filterKey: 'date-picker',
          option: {
            name,
            key: 'date-range',
            value: {
              from,
              to,
            },
          },
        });
      }
    }
  };

  useEffect(() => {
    const isDateRangeFilterActive =
      filterGroups.NEWS_SEARCH_FILTERS.activeFilterOptions.find(option => {
        return option.id === 'date-picker';
      });

    if (!isDateRangeFilterActive) {
      setDatePickerValues([null, null]);
    }
  }, [filterGroups]);

  return (
    <div
      className={styles.searchFiltersRoot}
      data-testid="search-filters"
      ref={refs.setReference}
      {...getReferenceProps()}
    >
      <Filters
        filterState={filterGroups.NEWS_SEARCH_FILTERS}
        store={useNewsSearchStore}
        group={'NEWS_SEARCH_FILTERS'}
        showCustomFilterModal={false}
        showAddMoreFilters={false}
        onCustomRangeClick={filter => {
          if (filter.key === 'date-picker') {
            openDatePicker();
          }
        }}
        source={POSTHOG_TRACKING_SOURCES.NEWS}
      />

      <MoreFilters
        filters={accordionFilters}
        moreFiltersSelectedCount={activeAccordionFiltersCount}
        toggleFilterOption={toggleFilterOption}
      />

      {(!!activeFiltersCount || !!activeAccordionFiltersCount) && (
        <ClearFiltersButton onClick={() => clearActiveFilters(t)} />
      )}

      {context.open && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <DatePicker
            value={datePickerValues}
            maxDate={new Date()}
            minDate={TEMP_MIN_FETCHING_NEWS_DATE}
            selectRange
            onDateChange={dateRange => {
              setDatePickerValues(dateRange);
              setShowDatePicker(false);
              setFilterOption(dateRange);
            }}
            locales={datePickerLocales}
            data-testid="calendar"
          />
        </div>
      )}
    </div>
  );
};
